import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Wrench, Factory, Calendar } from "lucide-react";
import Seo from "../components/Seo";
import { businessLd, personLd, breadcrumbLd } from "../components/StructuredData";

function useInView(threshold = 0.15) {
  const ref = React.useRef();
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const milestones = [
  {
    year: "1984",
    title: "The beginning",
    desc:
      "Ananda Paudel entered Nepal's furniture industry at 18, apprenticing under Kathmandu's most respected craftsmen — learning the trade from the ground up.",
  },
  {
    year: "1995",
    title: "Rising through the ranks",
    desc:
      "After a decade of dedicated work, Ananda was the interior designer and procurement specialist factories called when quality mattered.",
  },
  {
    year: "2008",
    title: "Industry authority",
    desc:
      "Two decades sourcing and designing for Nepal's largest furniture houses built an unmatched network of factory owners, suppliers, and craftsmen.",
  },
  {
    year: "2024",
    title: "Direct to you",
    desc:
      "With 40+ years of expertise, Ananda now works directly with clients — same factory contacts, same quality, none of the showroom markup.",
  },
];

const credentials = [
  {
    icon: <Award size={22} />,
    title: "40+ years of experience",
    desc:
      "Four decades inside Nepal's interior design and furniture industry — longer than most brands have existed.",
  },
  {
    icon: <Factory size={22} />,
    title: "Direct factory network",
    desc:
      "Personal relationships with vetted factories across Nepal mean manufacturer-direct quality and pricing.",
  },
  {
    icon: <Wrench size={22} />,
    title: "Bespoke designs",
    desc:
      "Describe your space and your taste — Ananda designs it, then has it built. Sizes, materials, finishes, all yours.",
  },
  {
    icon: <Users size={22} />,
    title: "Hundreds of homes furnished",
    desc:
      "From luxury residences in Lazimpat to executive offices in Lalitpur — Ananda has furnished spaces across Nepal.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Ananda Paudel — 40+ Years in Nepal's Furniture Industry"
        description="Meet Ananda Paudel — Nepal's most experienced interior designer and furniture specialist. 40+ years of factory-direct expertise, now working directly with homeowners."
        path="/about"
        jsonLd={[
          businessLd(),
          personLd(),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      <div className="min-h-screen bg-cream-50 pt-20 sm:pt-24">
        <div className="relative h-72 sm:h-96 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1600&q=80"
            alt="Bespoke furniture craftsmanship — Ananda Paudel, Nepal"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            width="1600"
            height="900"
          />
          <div className="absolute inset-0 bg-matte-900/60 flex items-center justify-center">
            <div className="text-center">
              <p className="font-sans text-xs tracking-[0.4em] text-walnut-300 uppercase mb-3">
                40 Years. One Craftsman.
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl text-white font-light">
                Ananda Paudel
              </h1>
              <p className="font-sans text-sm text-cream-300 mt-3 tracking-wider">
                Interior Designer · Furniture Specialist · Nepal
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <AnimatedSection className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
            <div>
              <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-4">
                Who is Ananda Paudel?
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl text-matte-900 font-light leading-snug mb-6">
                Nepal's most experienced furniture mind — now working directly for you
              </h2>
              <p className="font-sans text-sm text-matte-500 leading-relaxed mb-4">
                For over four decades, Ananda Paudel was the person Nepal's
                largest furniture companies trusted to source, design, and
                deliver their finest pieces. He knows every factory, every
                supplier, every craftsman worth knowing in this industry.
              </p>
              <p className="font-sans text-sm text-matte-500 leading-relaxed mb-4">
                Now, he works directly with homeowners — through a clean
                three-stage process: <strong>Consult</strong> to understand
                your space and budget, <strong>Design</strong> pieces tailored
                to your home, and <strong>Build</strong> them at the same
                factories he's known for forty years.
              </p>
              <p className="font-sans text-sm text-matte-500 leading-relaxed mb-8">
                When you work with Ananda, you're getting the undivided
                attention of a veteran who will design, build, and stand behind
                every piece personally.
              </p>
              <Link
                to="/book"
                className="inline-flex items-center gap-2 bg-walnut-600 text-cream-100 font-sans text-sm tracking-wider px-7 py-4 hover:bg-walnut-700 transition-colors group"
              >
                <Calendar size={14} />
                Book a Free Consultation
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=700&q=80"
                alt="Bespoke sofa craftsmanship by Ananda Paudel"
                className="w-full h-80 object-cover rounded-sm"
                loading="lazy"
                decoding="async"
                width="700"
                height="500"
              />
              <div className="absolute -bottom-4 -left-4 bg-walnut-600 text-cream-100 p-5 hidden sm:block">
                <p className="font-serif text-3xl">40+</p>
                <p className="font-sans text-xs tracking-wider uppercase mt-1">
                  Years of Expertise
                </p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="mb-24 bg-matte-900 text-cream-200 p-8 sm:p-12 rounded-sm">
            <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-4">
              Why no showroom is actually good for you
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white font-light leading-snug mb-6">
              "Your home is the showroom. My factory connections are your advantage."
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
              {[
                {
                  label: "No rent markups",
                  desc:
                    "Showrooms pass enormous overhead to you. Working directly with Ananda means you pay for the furniture — not someone's prime-location lease.",
                },
                {
                  label: "Made to order",
                  desc:
                    "Nothing sits in a warehouse. Every piece is designed and built specifically for you — your dimensions, your finish, your timeline.",
                },
                {
                  label: "Full transparency",
                  desc:
                    "Ananda tells you exactly what something costs to make, and why. No mystery pricing, no inflated 'original' prices with fake discounts.",
                },
              ].map((item) => (
                <div key={item.label} className="border border-matte-700 p-6 rounded-sm">
                  <h3 className="font-serif text-lg text-walnut-300 mb-3">{item.label}</h3>
                  <p className="font-sans text-sm text-matte-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection className="mb-24">
            <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-4 text-center">
              Why trust Ananda
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-matte-900 font-light text-center mb-12">
              Credentials that no store can match
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {credentials.map((item, i) => (
                <AnimatedSection key={i} delay={i * 80}>
                  <div className="group p-8 border border-cream-200 hover:border-walnut-300 transition-all duration-300 hover:shadow-lg rounded-sm bg-white flex gap-5">
                    <div className="w-12 h-12 rounded-full bg-walnut-100 text-walnut-500 flex items-center justify-center flex-shrink-0 group-hover:bg-walnut-400 group-hover:text-white transition-all duration-300">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-matte-900 mb-2">{item.title}</h3>
                      <p className="font-sans text-sm text-matte-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection className="mb-24">
            <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-4 text-center">
              The journey
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-matte-900 font-light text-center mb-14">
              40 years in the making
            </h2>
            <div className="relative">
              <div className="absolute left-16 top-0 bottom-0 w-px bg-cream-300 hidden sm:block" />
              <div className="space-y-10">
                {milestones.map((m, i) => (
                  <AnimatedSection key={i} delay={i * 100} className="flex gap-8 items-start">
                    <div className="flex-shrink-0 w-32 text-right hidden sm:block">
                      <span className="font-serif text-2xl text-walnut-500">{m.year}</span>
                    </div>
                    <div className="flex-shrink-0 w-3 h-3 rounded-full bg-walnut-400 mt-2 hidden sm:block relative z-10" />
                    <div className="flex-1 pb-4">
                      <span className="font-serif text-lg text-walnut-500 sm:hidden block mb-1">
                        {m.year}
                      </span>
                      <h3 className="font-serif text-xl text-matte-900 mb-2">{m.title}</h3>
                      <p className="font-sans text-sm text-matte-500 leading-relaxed">{m.desc}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="text-center">
            <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-4">
              Ready to begin?
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-matte-900 font-light mb-6">
              Tell Ananda what you need
            </h2>
            <p className="font-sans text-sm text-matte-500 max-w-xl mx-auto mb-8 leading-relaxed">
              Single sofa or whole home — start with a free 30-minute
              consultation. Bring your photos, your dimensions, your
              references. Ananda takes it from there.
            </p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-matte-900 text-cream-100 font-sans text-sm tracking-wider px-10 py-4 hover:bg-walnut-700 transition-colors group"
            >
              <Calendar size={14} />
              Book a Free Consultation
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </div>
    </>
  );
}
